import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserRolesService } from '../../../../core/services/user-roles.service';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, forkJoin, Observable, of, Subscription } from 'rxjs';

interface User {
  id: number;
  name: string;
  email: string;
  photo?: string;
  department?: string;
}

interface Role {
  id: number;
  name: string;
  description: string;
}

@Component({
  selector: 'em-role-assignment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DropdownModule,
    MultiSelectModule,
    ButtonModule,
    TranslateModule,
  ],
  templateUrl: './role-assignment-dialog.component.html',
  styleUrls: ['./role-assignment-dialog.component.scss'],
})
export class RoleAssignmentDialogComponent implements OnInit {
  roleAssignmentForm: FormGroup;
  users: User[] = [];
  roles: Role[] = [];
  isEditMode = false;
  editingSingleRole = false;
  existingUserRole: any = null;
  existingAssignment: any = null;
  preSelectedUser: User | null = null;
  preSelectedRole: Role | null = null;
  selectedSingleRole: Role | null = null;
  loading = false;

  constructor(
    private fb: FormBuilder,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private messageService: MessageService,
    private userRolesService: UserRolesService,
    private translate: TranslateService,
  ) {
    this.roleAssignmentForm = this.fb.group({
      selectedUser: ['', Validators.required],
      selectedRoles: [[], Validators.required],
    });
  }

  ngOnInit(): void {
    if (this.config.data) {
      this.users = this.config.data.users || [];
      this.roles = this.config.data.roles || [];
      this.isEditMode = this.config.data.isEditMode || false;
      this.editingSingleRole = this.config.data.editingSingleRole || false;
      this.existingUserRole = this.config.data.existingUserRole || null;
      this.existingAssignment = this.config.data.existingAssignment || null;
      this.preSelectedUser = this.config.data.preSelectedUser || null;
      this.preSelectedRole = this.config.data.preSelectedRole || null;

      if (this.editingSingleRole && this.existingAssignment) {
        this.selectedSingleRole = this.existingAssignment.role;
        this.roleAssignmentForm.patchValue({
          selectedUser: this.existingAssignment.user,
          selectedRoles: [this.existingAssignment.role],
        });
      } else if (this.isEditMode && this.existingUserRole) {
        this.roleAssignmentForm.patchValue({
          selectedUser: this.existingUserRole.user,
          selectedRoles: this.existingUserRole.roles || [],
        });
      } else if (this.preSelectedUser) {
        this.roleAssignmentForm.patchValue({
          selectedUser: this.preSelectedUser,
          selectedRoles: [],
        });
      }
    }
  }

  onImageError(event: any, userName: string): void {
    const target = event.target as HTMLImageElement;
    const initials = userName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
    target.style.display = 'none';

    const fallback = document.createElement('div');
    fallback.className =
      'w-2 h-2 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-medium';
    fallback.style.cssText = 'width: 8px !important; height: 8px !important; font-size: 6px !important;';
    fallback.textContent = initials;
    target.parentNode?.insertBefore(fallback, target);
  }

  onSave(): void {
    if (this.roleAssignmentForm.valid) {
      const formValue = this.roleAssignmentForm.value;
      const user = formValue.selectedUser;
      const roles = formValue.selectedRoles;

      if (!user || !roles || roles.length === 0) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.error'),
          detail: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.selectUserAndRole'),
        });
        return;
      }

      const roleIds = roles.map((r: Role) => r.id);
      /** ✅ Detect no change in edit mode */
      if (this.isEditMode && this.existingUserRole) {
        const previousRoles = (this.existingUserRole.roles || []).map((r: Role) => r.id).sort();
        const currentRoles = roleIds.slice().sort();

        const noChanges =
          previousRoles.length === currentRoles.length &&
          previousRoles.every((id: number, index: number) => id === currentRoles[index]);

        if (noChanges) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
            detail: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage'),
          });
          return;
        }
      }

      if (this.editingSingleRole) {
        console.log('roleIds 1' + roleIds);
        this.userRolesService.updateUserRoles(user.id, roleIds, 'UI').subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.success'),
              detail: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.updateSuccess', {
                name: user.name,
              }),
            });
            this.ref.close({
              success: true,
              action: 'edit',
              data: {
                user: user,
                role: this.roleAssignmentForm.get('selectedRole')?.value || null,
              },
            });
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.error'),
              detail: this.getApiErrorMessage(
                err,
                this.translate.instant('layout.configurations.users.assignRolesToUser.roles.updateError'),
              ),
            });
            this.ref.close({ success: false, action: 'edit', error: err });
          },
        });
      } else {
        if (this.isEditMode) {
          const previousRoleIds = (this.existingUserRole.roles || []).map((r: Role) => r.id);
          const currentRoleIds = roles.map((r: Role) => r.id);

          const rolesToAdd = currentRoleIds.filter((id: any) => !previousRoleIds.includes(id));
          const rolesToRemove = previousRoleIds.filter((id: any) => !currentRoleIds.includes(id));
          
          this.loading = true;
          const add$ = rolesToAdd.length
            ? this.userRolesService.assignRolesToUser(user.id, rolesToAdd, 'UI')
            : of(null);

          const remove$ = rolesToRemove.length
            ? forkJoin(rolesToRemove.map((roleId: number) => this.userRolesService.removeUserRole(user.id, roleId)))
            : of(null);

          forkJoin([add$, remove$])
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
              next: () => {
                const message = this.translate.instant(
                  'layout.configurations.users.assignRolesToUser.roles.updateMultipleSuccess',
                  {
                    name: user.name,
                  },
                );

                this.messageService.add({
                  severity: 'success',
                  summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.success'),
                  detail: message,
                });
                this.ref.close({
                  success: true,
                  action: 'edit',
                  data: {
                    user: user,
                    roles: this.roleAssignmentForm.get('selectedRoles')?.value || [],
                  },
                });
              },
              error: (err: HttpErrorResponse) => {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.error'),
                  detail: this.getApiErrorMessage(
                    err,
                    this.translate.instant('layout.configurations.users.assignRolesToUser.roles.updateError'),
                  ),
                });
                this.ref.close({ success: false, action: 'edit', error: err });
              },
            });
        } else {
          this.userRolesService.assignRolesToUser(user.id, roleIds, 'UI').subscribe({
            next: () => {
              const action = 'create';
              const message = this.translate.instant(
                'layout.configurations.users.assignRolesToUser.roles.assignSuccess',
                {
                  name: user.name,
                },
              );

              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.success'),
                detail: message,
              });
              this.ref.close({
                success: true,
                action,
                data: {
                  user: user,
                  roles: this.roleAssignmentForm.get('selectedRoles')?.value || [],
                },
              });
            },
            error: (err: HttpErrorResponse) => {
              const action = 'create';
              const message = this.getApiErrorMessage(
                err,
                this.translate.instant('layout.configurations.users.assignRolesToUser.roles.assignError'),
              );
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.error'),
                detail: message,
              });
              this.ref.close({ success: false, action, error: err });
            },
          });
        }
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.error'),
        detail: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.fillRequired'),
      });
    }
  }

  onSingleRoleChange(event: any): void {
    const selectedRole = event.value;
    this.roleAssignmentForm.patchValue({
      selectedRoles: [selectedRole],
    });
  }

  onMultiRoleChange(event: any): void {
    this.roleAssignmentForm.patchValue({
      selectedRoles: event.value,
    });
  }

  onCancel(): void {
    this.ref.close({ action: 'cancel' });
  }

  private getApiErrorMessage(err: HttpErrorResponse, fallback: string): string {
    const apiMessage = err.error?.message || err.error?.detail || err.message;
    return (apiMessage && apiMessage.trim()) || fallback;
  }
}
